import { JoinInput, JoinPhoneInput } from '../../../components/items/joinInput';
import { FileUploadImg } from '../../../components/items/uploadImg';
import { InputBtn } from '../../../components/items/inputBtn';
import { Container, Title } from './join';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatPhoneNumber, idRegex } from '../../../utils/util';
import { useEffect, useState } from 'react';
import {
  FetchCorporationList,
  checkPassid,
  companySignup,
  requestPhoneAuth,
  uploadCompanyRegistration,
} from '../../../api/join';
import { AlertModal } from '../../../components/modal/alert.modal';
import { useHistory } from 'react-router-dom';
import { joinCompanySchema } from '../../../schema/joinSchema';
import Select from 'react-select';

// 데이터 타입 정의
// 회사명 = select 로 회사 선택으로 변경하고, 회사명 받아 올 때 주소를 가져와 detail에 넣어 주면됨.
// detail은 회사 주소로 title 변경
interface JoinFormValues {
  passid: string; // 아이디
  password: string; // 로그인 패스워드
  passwordConfirm: string; // 비밀번호 확인
  phone: string; // 전화번호
  registrationFile: File | null; // 사업장 등록증 파일
  name: string; // 기업명
  address: string; // 주소
  detail: string; // 상세 주소
  charge: string; // 담당자
  position: string; // 직책
  authCode?: string; // 인증번호 필드 추가 (optional)
}

interface CorporationList {
  corporationId: number;
  name: string;
  address: string;
  detail: string;
}

const JoinCompany = () => {
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [phoneAuthUUID, setPhoneAuthUUID] = useState<string | null>(null); // 인증 UUID 상태 추가
  const [isPhoneVerified, setIsPhoneVerified] = useState(false); // 전화번호 인증 여부 상태 추가
  const [isPhoneDisabled, setIsPhoneDisabled] = useState(false);
  const [isSignupComplete, setIsSignupComplete] = useState(false); // 회원 가입 완료 여부 상태
  const [isIdChecked, setIsIdChecked] = useState(false); // 중복 확인 여부 상태 추가
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null); // 타이머 상태 추가
  const [remainingTime, setRemainingTime] = useState(180); // 남은 시간(초) 상태 추가
  const [corporationList, setCorporationList] = useState<CorporationList[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    formState: { errors },
  } = useForm<JoinFormValues>({
    resolver: zodResolver(joinCompanySchema),
  });

  const onSubmit = async (data: JoinFormValues) => {
    if (!isIdChecked) {
      setModalMessage('아이디 중복 확인을 완료해주세요.');
      setIsModalOpen(true);
      return; // 중복 확인이 완료되지 않은 경우 회원가입 진행 중단
    }

    if (!isPhoneVerified) {
      setModalMessage('휴대폰 번호 인증을 완료해주세요.');
      setIsModalOpen(true);
      return; // 인증이 완료되지 않은 경우 회원가입 진행 중단
    }

    try {
      let registrationUrl = '';

      if (data.registrationFile) {
        // 1. 자동차 등록증 파일 업로드
        registrationUrl = await uploadCompanyRegistration(
          data.registrationFile,
        );
      }

      // 2. 회원가입 API 호출
      const response = await companySignup({
        passid: data.passid,
        password: data.password,
        phone: data.phone,
        registration: registrationUrl,
        name: data.name,
        address: data.address,
        detail: data.detail,
        charge: data.charge,
        position: data.position,
      });

      history.push('/joinComplate');
      console.log(response.message); // 성공 메시지 출력
    } catch (error) {
      setModalMessage('회원가입에 실패했습니다. 관리자에게 문의해주세요.');
      setIsModalOpen(true);
      console.error('회원가입 실패:', error);
    }
  };

  const idCheckHandler = async () => {
    try {
      const passid = getValues('passid'); // 현재 입력된 차량 번호 값 가져오기
      if (passid) {
        // 1. 차량 번호 형식 검증 (carNoValPartial)
        const isValidPassid = idRegex.test(passid);
        if (isValidPassid) {
          setModalMessage('아이디에 한글, 특수문자는 불가능합니다.');
          setIsModalOpen(true); // 차량 번호 형식 오류 모달 열기
          return; // 차량 번호 형식이 잘못된 경우 중복 확인을 진행하지 않음
        }

        // 2. 차량 번호 중복 확인 API 호출
        const message = await checkPassid(passid);
        setModalMessage(message);
        setIsModalOpen(true); // 중복 확인 결과 모달 열기
        setIsIdChecked(true); // 중복 확인이 성공적으로 완료되었음을 표시
      } else {
        setModalMessage('아이디를 입력해주세요.');
        setIsModalOpen(true); // 중복 확인 결과 모달 열기
      }
    } catch (error: any) {
      if (error.response && error.response.status === 409) {
        setModalMessage('아이디는 이미 사용 중입니다.');
      } else {
        console.error('아이디 중복 확인 실패:', error);
        setModalMessage('아이디 중복 확인에 실패하였습니다.');
      }
      setIsModalOpen(true);
    }
  };

  const startTimer = () => {
    if (timer) {
      clearTimeout(timer);
    }

    setRemainingTime(180); // 타이머 시작 시 3분 설정

    const newTimer = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(newTimer);
          setPhoneAuthUUID(null);
          setModalMessage('인증 시간이 만료되었습니다. 다시 시도해주세요.');
          setIsModalOpen(true);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000); // 1초마다 타이머 업데이트

    setTimer(newTimer);
  };

  const phoneAuth = async () => {
    try {
      const phone = getValues('phone');
      if (phone) {
        const uuid = await requestPhoneAuth(phone, 'driver');
        setPhoneAuthUUID(uuid);
        setModalMessage('인증번호가 전송되었습니다. 번호를 확인해주세요.');
        setIsModalOpen(true);
        startTimer(); // 타이머 시작
      } else {
        setModalMessage('휴대폰 번호를 입력해주세요.');
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error('휴대폰 본인 인증 요청 실패:', error);
      setModalMessage('본인 인증 요청에 실패했습니다.');
      setIsModalOpen(true);
    }
  };

  const verifyAuthCode = async (authCode: string) => {
    if (!phoneAuthUUID) {
      setModalMessage('인증 시간이 만료되었습니다. 다시 시도해주세요.');
      setIsModalOpen(true);
      return;
    }

    try {
      // const message = await verifyPhoneAuth(phoneAuthUUID, authCode);

      setModalMessage('본인 인증에 성공하였습니다.');
      setIsModalOpen(true);
      setIsPhoneVerified(true);
      setIsPhoneDisabled(true);

      if (timer) {
        clearTimeout(timer); // 인증 성공 시 타이머 해제
      }
    } catch (error) {
      console.error('인증 실패:', error);
      setModalMessage('본인 인증 요청에 실패하였습니다.');
      setIsModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsSignupComplete(false); // 모달을 닫을 때 회원가입 완료 상태를 초기화
  };

  useEffect(() => {
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [timer]);

  useEffect(() => {
    const getCorporationList = async () => {
      try {
        const response = await FetchCorporationList();
        setCorporationList(response);
      } catch (error) {
        console.error(error);
      }
    };

    getCorporationList();
  }, []);

  return (
    <Container>
      <Title>
        <h3>회사 회원 정보</h3>
        <span>회원 여부 확인 및 가입을 진행합니다.</span>
      </Title>
      <form onSubmit={handleSubmit(onSubmit)}>
        <JoinInput
          name="passid"
          label="아이디"
          btn
          placeholder="아이디를 입력해주세요."
          errors={
            errors.passid?.message ? [errors.passid.message as string] : []
          }
          onClick={idCheckHandler}
          register={register}
        />
        <JoinInput
          name="password"
          label="비밀번호"
          placeholder="비밀번호 입력"
          type="password"
          errors={
            errors.password?.message ? [errors.password.message as string] : []
          }
          register={register}
        />
        <JoinInput
          name="passwordConfirm"
          label="비밀번호 확인"
          placeholder="비밀번호 입력 확인"
          type="password"
          errors={
            errors.passwordConfirm?.message
              ? [errors.passwordConfirm.message as string]
              : []
          }
          register={register}
        />
        <JoinPhoneInput
          name="phone"
          label="휴대폰 번호"
          placeholder="휴대폰 번호 입력"
          type="text"
          btn
          btnTitle="본인인증"
          disabled={isPhoneDisabled}
          errors={errors.phone?.message ? [errors.phone.message as string] : []}
          register={register}
          validation={{
            onChange: (e: any) => {
              const formattedValue = formatPhoneNumber(e.target.value);
              setValue('phone', formattedValue, { shouldValidate: true });
            },
          }}
          onClick={phoneAuth}
          className={`input-disabled ${isPhoneDisabled ? 'disabled' : ''}`}
        />
        {/* 인증 번호 입력 필드 */}
        {phoneAuthUUID && !isPhoneVerified && (
          <div className="timer-wrapper">
            <JoinInput
              name="authCode"
              label="인증번호"
              placeholder="인증번호 입력"
              type="text"
              btn
              btnTitle="인증확인"
              register={register}
              onClick={() => verifyAuthCode(getValues('authCode') || '')}
            />
            <p>
              남은 시간: {Math.floor(remainingTime / 60)}:
              {remainingTime % 60 < 10
                ? `0${remainingTime % 60}`
                : remainingTime % 60}
            </p>
          </div>
        )}
        <div className="select-wrapper">
          <span>회사 선택</span>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Select
                className="search-select"
                classNamePrefix="react-select"
                options={corporationList.map((corp) => ({
                  value: corp.name, // corporationName을 value로 설정
                  label: corp.name, // corporationName을 label로 설정
                }))}
                onChange={(selectedOption) => {
                  field.onChange(selectedOption ? selectedOption.value : null);

                  // 선택된 회사 찾기
                  const selectedCompany = corporationList.find(
                    (corp) => corp.name === selectedOption?.value,
                  );

                  if (selectedCompany) {
                    // 선택된 회사 정보를 기반으로 다른 필드 값을 설정
                    setValue('address', selectedCompany.address);
                    setValue('detail', selectedCompany.detail);
                  }
                }}
                value={corporationList
                  .map((corp) => ({
                    value: corp.name,
                    label: corp.name,
                  }))
                  .find((option) => option.value === field.value)} // 현재 선택된 값을 설정
                components={{ IndicatorSeparator: () => null }}
                placeholder="회사 선택"
                menuPlacement="top"
                maxMenuHeight={100}
              />
            )}
          />
        </div>
        <JoinInput
          name="address"
          label="회사 주소"
          placeholder="회사 주소 입력"
          type="text"
          btnTitle="주소 검색"
          register={register}
          errors={
            errors.address?.message ? [errors.address.message as string] : []
          }
          className="input-disabled disabled"
        />
        <JoinInput
          name="detail"
          label="상세 주소"
          placeholder="상세 주소 입력"
          type="text"
          register={register}
          errors={
            errors.detail?.message ? [errors.detail.message as string] : []
          }
          className="input-disabled disabled"
        />
        <JoinInput
          name="charge"
          label="담당자 이름"
          placeholder="담당자 이름 입력"
          type="text"
          register={register}
          errors={
            errors.charge?.message ? [errors.charge.message as string] : []
          }
        />
        <JoinInput
          name="position"
          label="직책"
          placeholder="직책 입력"
          type="text"
          register={register}
          errors={
            errors.position?.message ? [errors.position.message as string] : []
          }
        />
        <FileUploadImg
          title="사업자 등록증 첨부하기"
          height="180"
          name="registrationFile"
          register={register}
          setValue={setValue}
          errors={
            errors.registrationFile?.message
              ? [errors.registrationFile.message as string]
              : []
          }
        />
        <InputBtn name="회원가입 완료" type="submit" />
      </form>

      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={
            isSignupComplete
              ? () => history.push('/joinComplete')
              : handleModalClose
          } // 회원가입 완료 시에만 로그인 화면으로 이동
        />
      )}
    </Container>
  );
};

export default JoinCompany;
